import type { NotificationPayload, NotificationRecord } from "@shared/types";
import * as Ably from "ably";
import {
	createRealtimeAuthToken,
	type RealtimeAuthTokenResponse,
} from "./notifications-api.service";

export interface NotificationsRealtimeConnection {
	disconnect: () => void;
}

export async function connectNotificationsRealtime(options: {
	onNotification: (notification: NotificationRecord) => void;
	onConnected?: () => void;
	onError?: (error: unknown) => void;
}): Promise<NotificationsRealtimeConnection> {
	const initialAuth = await createRealtimeAuthToken();
	let nextAuth: RealtimeAuthTokenResponse | null = initialAuth;

	const client = new Ably.Realtime({
		authCallback: async (_tokenParams, callback) => {
			try {
				const auth = nextAuth ?? (await createRealtimeAuthToken());
				nextAuth = null;
				callback(null, auth.tokenRequest);
			} catch (error) {
				options.onError?.(error);
				callback("Realtime auth failed", null);
			}
		},
	});

	const channel = client.channels.get(initialAuth.channel);
	const connectedListener = () => options.onConnected?.();
	const failedListener = (stateChange: unknown) =>
		options.onError?.(stateChange);

	client.connection.on("connected", connectedListener);
	client.connection.on("failed", failedListener);

	await channel.subscribe("notification.created", (message) => {
		const payload = message.data as NotificationPayload;
		if (payload?.notification) {
			options.onNotification(payload.notification);
		}
	});

	return {
		disconnect: () => {
			channel.unsubscribe("notification.created");
			client.connection.off("connected", connectedListener);
			client.connection.off("failed", failedListener);
			client.close();
		},
	};
}
